import type { FC } from "react";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import { downloadFileMutationOptions } from "../api/files";
import type { DocumentReference } from "../dynamicForm/types";
import { DocumentLink, DownloadFileMessage } from "../dynamicShared/DocumentLink";
import { PencilIcon, TrashCanIcon } from "./icons";

const ACTION_BUTTONS_CLASS_NAME = "action-buttons";

type DocumentItemProps = {
  reference: DocumentReference;
  onRemoveClick: () => void;
  onEditClick: () => void;
  isReadOnly: boolean;
  isRemoving?: boolean;
  linkSx?: SxProps<Theme>;
};

export const DocumentItem: FC<DocumentItemProps> = ({
  reference,
  onRemoveClick,
  onEditClick,
  isReadOnly,
  isRemoving,
  linkSx,
}) => {
  const { t } = useTranslation();
  const isFile = reference.kind === "file";
  const editLabel = isFile ? t("Document.Edit.Tooltip") : t("ExternalMultipleLink.EditLinkTitle.Tooltip");
  const removeLabel = isFile ? t("Document.Delete.Tooltip") : t("ExternalMultipleLink.DeleteLinkTitle.Tooltip");
  const downloadFileMutation = useMutation(downloadFileMutationOptions());

  return (
    <Box>
      <Box
        sx={{
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          ...(!isReadOnly && {
            "&:hover": { backgroundColor: theme => theme.palette.action.hover },
            [`& .${ACTION_BUTTONS_CLASS_NAME}`]: { opacity: isRemoving ? 1 : 0 },
            [`&:is(:hover, :focus-within) .${ACTION_BUTTONS_CLASS_NAME}`]: { opacity: 1 },
          }),
        }}
      >
        <DocumentLink reference={reference} downloadFileMutation={downloadFileMutation} sx={linkSx} />
        <Stack
          direction="row"
          className={ACTION_BUTTONS_CLASS_NAME}
          sx={{ gap: 0.5, visibility: isReadOnly ? "hidden" : "visible" }}
        >
          <Tooltip title={editLabel}>
            <IconButton aria-label={editLabel} onClick={onEditClick}>
              <PencilIcon size={14} />
            </IconButton>
          </Tooltip>
          <Tooltip title={removeLabel}>
            <IconButton aria-label={removeLabel} onClick={onRemoveClick} loading={isRemoving}>
              <TrashCanIcon size={14} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <DownloadFileMessage downloadFileMutation={downloadFileMutation} />
    </Box>
  );
};
